import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, message } from 'antd';
import {
	plannedMaintenanceSchedulesGroupedRestCrudThunksForBuyer,
	plannedMaintenanceSchedulesGroupedRestCrudThunksForSupplier,
} from '../../thunks/planned_maintenance_schedule_groups_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import { PlannedMaintenanceScheduleGroupRowDisplay } from '../planned_maintenance_schedule_group_row_display/PlannedMaintenanceScheduleGroupRowDisplay';
import { PLANNED_MAINTENANCE_SCHEDULE_GROUPS_CRUD_ACTION_CREATORS } from '../../actions/planned_maintenance_schedule_groups_actions';
import { ROLE_TYPES } from '../../utils/DataConstants';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { DownloadOutlined } from '@ant-design/icons';
import { exportPlannedMaintenanceScheduleCSV } from '../../thunks/planned_maintenance_schedules_thunks';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { useFilters } from '../../hooks/useFilters';
import { getPMScheduleFilterConfig } from '../../utils/PMScheduleFilterUtils';
import { safeEncodeURIComponent } from '../../utils/HistoryUtils';

require('./PlannedMaintenanceSchedulesIndexPage.less');

const PLANNED_MAINTENANCE_SCHEDULES_OVERVIEW_TARGET_COLLECTION =
	'plannedMaintenanceScheduleGroupsIndex';
const PM_ADVANCED_FILTERS = 'plannedMaintenanceAdvancedFilters';

const PlannedMaintenanceSchedulesIndexPage: FC<any> = ({
	history,
	location,
	updateFilters,
	updateSorters,
	clearAndUpdateFilters,
	plannedMaintenanceScheduleGroups,
	userType,
	exportPMSchedules,
}): React.ReactElement => {
	const currentUser = useSelector((state: any) => state.session.currentUser);
	const [exporting, setExporting] = useState(false);

	const downloadCSV = useCallback(() => {
		const filters = nullSafeGetOrElse(
			`${PLANNED_MAINTENANCE_SCHEDULES_OVERVIEW_TARGET_COLLECTION}.filters`,
			plannedMaintenanceScheduleGroups,
			{}
		);
		setExporting(true);
		exportPMSchedules(filters)
			.catch(message.error)
			.finally(() => setExporting(false));
	}, [exportPMSchedules, plannedMaintenanceScheduleGroups]);

	const columns = useMemo(
		() => [
			{
				title: 'Template Name',
				dataIndex: 'plannedMaintenanceTemplateId',
				render: (_, record) => (
					<PlannedMaintenanceScheduleGroupRowDisplay plannedMaintenanceScheduleGroup={record} />
				),
			},
		],
		[]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				let searchString = location.search;
				if (searchString[0] === '?') {
					searchString = searchString.slice(1);
				}
				let backUrl = `/${userType}/plannedMaintenance/detail/${safeEncodeURIComponent(
					record.title
				)}/${encodeURIComponent(record.frequency)}/${encodeURIComponent(
					record.plannedMaintenanceTemplateId
				)}/${encodeURIComponent(record.basedOn)}/details`;
				if (searchString && searchString.length > 0) {
					backUrl = backUrl.concat(`?backlinkParams=${encodeURIComponent(searchString)}`);
				}
				history.push(backUrl);
			},
		}),
		[history, location.search, userType]
	);

	const pmScheduleFilters = useFilters({
		filterTargetCollectionName: PM_ADVANCED_FILTERS,
		overviewCollectionName: PLANNED_MAINTENANCE_SCHEDULES_OVERVIEW_TARGET_COLLECTION,
		updateFilters: useCallback(
			(filters) => updateFilters(filters, PLANNED_MAINTENANCE_SCHEDULES_OVERVIEW_TARGET_COLLECTION),
			[updateFilters]
		),
	});

	const filterConfig = useMemo(
		() => getPMScheduleFilterConfig({ filters: pmScheduleFilters, currentUser }),
		[pmScheduleFilters, currentUser]
	);

	const handleCreateSchedule = useCallback(() => {
		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}
		let newUrl = `/${userType}/plannedMaintenance/new`;
		if (searchString && searchString.length > 0) {
			newUrl = newUrl.concat(`?backlinkParams=${encodeURIComponent(searchString)}`);
		}
		history.push(newUrl);
	}, [history, location.search, userType]);

	return (
		<PaginatedReduxTableWithHeader
			targetCollectionName={PLANNED_MAINTENANCE_SCHEDULES_OVERVIEW_TARGET_COLLECTION}
			stateSlice={plannedMaintenanceScheduleGroups}
			updateFilters={updateFilters}
			updateSorters={updateSorters}
			clearAndUpdateFilters={clearAndUpdateFilters}
			filterConfig={filterConfig}
			entityCollectionName="planned_maintenance_schedules/grouped"
			tableColumns={columns}
			fetchData={
				userType === ROLE_TYPES.SUPPLIER
					? plannedMaintenanceSchedulesGroupedRestCrudThunksForSupplier.read
					: plannedMaintenanceSchedulesGroupedRestCrudThunksForBuyer.read
			}
			onTableRow={onRow}
			keyAccessor={(record) =>
				record['title'] + ',' + record['frequency'] + ',' + record['plannedMaintenanceTemplateId']
			}
			initialPagination={{ current: 1, pageSize: 10 }}
			advancedFiltersInputParamName="title"
			rightActions={
				<div className="flex flex-row items-center">
					<Button
						size="large"
						icon={<DownloadOutlined />}
						className="inline-block-visible-md mr-2"
						loading={exporting}
						onClick={downloadCSV}
					>
						<span className="inline-block-visible-xl">Export CSV</span>
					</Button>
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PM}>
						<Button
							size="large"
							type="primary"
							onClick={() => {
								let searchString = location.search;
								if (searchString[0] === '?') {
									searchString = searchString.slice(1);
								}
								let newUrl = `/${userType}/plannedMaintenance/new`;
								if (searchString && searchString.length > 0) {
									newUrl = newUrl.concat(`?backlinkParams=${encodeURIComponent(searchString)}`);
								}
								history.push(newUrl);
							}}
						>
							Create Schedule
						</Button>
					</AccessPermissionChecker>
				</div>
			}
			emptyState={
				<EmptyState
					graphic={
						<img
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/calendar.svg"
							alt="Your team hasn't scheduled any planned maintenance yet."
						/>
					}
					headline={'Proper planning prevents poor performance.'}
					body={
						<div style={{ textAlign: 'center' }}>
							<div style={{ maxWidth: 440, marginBottom: 16 }}>
								Your team hasn't scheduled any planned maintenance yet. Create your first to stay on
								top of your PMs.
							</div>
							<Button type="primary" onClick={handleCreateSchedule}>
								Create schedule
							</Button>
						</div>
					}
				/>
			}
		/>
	);
};

const mapStateToProps = (state) => ({
	plannedMaintenanceScheduleGroups: state['planned_maintenance_schedules/grouped'],
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(
			PLANNED_MAINTENANCE_SCHEDULE_GROUPS_CRUD_ACTION_CREATORS.updateFilters(
				filters,
				targetCollection
			)
		),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(
			PLANNED_MAINTENANCE_SCHEDULE_GROUPS_CRUD_ACTION_CREATORS.updateSorting(
				sortBy,
				order,
				targetCollection
			)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			PLANNED_MAINTENANCE_SCHEDULE_GROUPS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
	exportPMSchedules: (filters) =>
		dispatch(exportPlannedMaintenanceScheduleCSV(ownProps.userType)({}, filters)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(PlannedMaintenanceSchedulesIndexPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
